import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ContentLayout,
  Header,
  Button,
  SpaceBetween,
  Container,
  ColumnLayout,
  Box,
  Badge,
  Link,
  Alert,
  Tabs,
  Modal,
  FormField,
  Input
} from '@cloudscape-design/components';
import { titleAPI } from '../services/api';
import VersionsTab from '../components/VersionsTab';

const TitleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState(null);
  const [parentTitle, setParentTitle] = useState(null);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showImdbModal, setShowImdbModal] = useState(false);
  const [imdbId, setImdbId] = useState('');
  const [imdbLoading, setImdbLoading] = useState(false);
  const [imdbError, setImdbError] = useState('');

  useEffect(() => {
    const loadTitle = async () => {
      try {
        const response = await titleAPI.getTitle(id);
        const titleData = response.data;
        setTitle(titleData);
        
        // Load parent if exists
        if (titleData.parent_id) {
          try {
            const parentResp = await titleAPI.getTitle(titleData.parent_id);
            setParentTitle(parentResp.data);
          } catch (e) {
            console.log('Could not load parent');
          }
        } else {
          setParentTitle(null);
        }
        
        // Load children if exists
        if (titleData.children_ids && titleData.children_ids.length > 0) {
          const childPromises = titleData.children_ids.map(cid => 
            titleAPI.getTitle(cid).then(r => r.data).catch(() => null)
          );
          const childResults = await Promise.all(childPromises);
          setChildren(childResults.filter(Boolean));
        } else {
          setChildren([]);
        }
      } catch (error) {
        setError('Failed to load title');
      } finally {
        setLoading(false);
      }
    };

    loadTitle();
  }, [id]);

  // Determine what child type can be added
  const getChildTypeInfo = () => {
    if (!title) return null;
    switch (title.content_type) {
      case 'series': return { type: 'season', label: 'Add Season' };
      case 'season': return { type: 'episode', label: 'Add Episode' };
      default: return null;
    }
  };

  const handleAddChild = () => {
    const childInfo = getChildTypeInfo();
    if (!childInfo) return;
    const params = new URLSearchParams({
      parentId: title.title_id,
      parentType: title.content_type,
      childType: childInfo.type,
      parentTitle: title.metadata?.title || title.title_id
    });
    navigate(`/create?${params.toString()}`);
  };

  const handleAddFromImdb = async () => {
    if (!imdbId.trim()) return;
    setImdbLoading(true);
    setImdbError('');
    try {
      const response = await titleAPI.enrichFromIMDb({
        imdb_id: imdbId.trim(),
        parent_id: title.title_id
      });
      const newTitle = response.data.content;
      setShowImdbModal(false);
      setImdbId('');
      navigate(`/titles/${newTitle.title_id}`);
    } catch (err) {
      setImdbError(err.response?.data?.error || 'Failed to create from IMDb');
    } finally {
      setImdbLoading(false);
    }
  };

  if (loading) return <Box>Loading...</Box>;
  if (error) return <Alert type="error">{error}</Alert>;
  if (!title) return <Alert type="error">Title not found</Alert>;

  const childTypeInfo = getChildTypeInfo();

  return (
    <>
    <ContentLayout
      header={
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => navigate('/')}>Back to List</Button>
              {childTypeInfo && (
                <Button onClick={handleAddChild}>
                  {childTypeInfo.label}
                </Button>
              )}
              {childTypeInfo && (
                <Button onClick={() => setShowImdbModal(true)} iconName="search">
                  Add from IMDb
                </Button>
              )}
              <Button 
                variant="primary" 
                onClick={() => navigate(`/edit/${id}`)}
              >
                Edit
              </Button>
            </SpaceBetween>
          }
        >
          {title.metadata?.title || title.title_id}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Container>
          <ColumnLayout columns={2}>
            <div>
              {title.metadata?.poster_url && (
                <Box textAlign="center">
                  <img 
                    src={title.metadata.poster_url} 
                    alt={title.metadata.title}
                    style={{ maxWidth: '300px', height: 'auto' }}
                  />
                </Box>
              )}
            </div>
            <SpaceBetween size="m">
              <div>
                <Box variant="h3">Basic Information</Box>
                <ColumnLayout columns={2}>
                  <div>
                    <Box variant="awsui-key-label">Title ID</Box>
                    <Box>{title.title_id}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Type</Box>
                    <Badge>{title.content_type}</Badge>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Year</Box>
                    <Box>{title.metadata?.year || '-'}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Status</Box>
                    <Badge color="blue">{title.metadata?.status || 'draft'}</Badge>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Language</Box>
                    <Box>{title.metadata?.language || '-'}</Box>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Country</Box>
                    <Box>{title.metadata?.country || '-'}</Box>
                  </div>
                </ColumnLayout>
              </div>

              {title.metadata?.genre && (
                <div>
                  <Box variant="awsui-key-label">Genres</Box>
                  <SpaceBetween direction="horizontal" size="xs">
                    {title.metadata.genre.map(g => (
                      <Badge key={g}>{g}</Badge>
                    ))}
                  </SpaceBetween>
                </div>
              )}

              {title.metadata?.synopsis && (
                <div>
                  <Box variant="awsui-key-label">Synopsis</Box>
                  <Box>{title.metadata.synopsis}</Box>
                </div>
              )}
            </SpaceBetween>
          </ColumnLayout>
        </Container>

        {/* Parent navigation */}
        {parentTitle && (
          <Container header={<Box variant="h3">📁 Parent</Box>}>
            <SpaceBetween direction="horizontal" size="m" alignItems="center">
              <Badge>{parentTitle.content_type}</Badge>
              <Button variant="link" onClick={() => navigate(`/titles/${parentTitle.title_id}`)}>
                {parentTitle.metadata?.title || parentTitle.title_id}
              </Button>
              {parentTitle.metadata?.year && <Box color="text-body-secondary">({parentTitle.metadata.year})</Box>}
            </SpaceBetween>
          </Container>
        )}

        {/* Children navigation */}
        {(children.length > 0 || childTypeInfo) && (
          <Container header={
            <Header
              actions={
                childTypeInfo && (
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button onClick={() => setShowImdbModal(true)} iconName="search">
                      Add from IMDb
                    </Button>
                    <Button onClick={handleAddChild} iconName="add-plus">
                      {childTypeInfo.label}
                    </Button>
                  </SpaceBetween>
                )
              }
            >
              {title.content_type === 'series' ? '📺 Seasons' : 
               title.content_type === 'season' ? '🎬 Episodes' : '📂 Children'} 
              ({children.length})
            </Header>
          }>
            <SpaceBetween size="xs">
              {children
                .sort((a, b) => {
                  const numA = a.metadata?.season_number || a.metadata?.episode_number || 0;
                  const numB = b.metadata?.season_number || b.metadata?.episode_number || 0;
                  return numA - numB;
                })
                .map(child => (
                <Box key={child.title_id} padding={{ vertical: 'xxs' }}>
                  <SpaceBetween direction="horizontal" size="m" alignItems="center">
                    <Badge color={child.content_type === 'episode' ? 'green' : 'blue'}>
                      {child.content_type}
                    </Badge>
                    <Button variant="link" onClick={() => navigate(`/titles/${child.title_id}`)}>
                      {child.metadata?.title || child.title_id}
                    </Button>
                    {child.metadata?.imdb_rating && (
                      <Box color="text-body-secondary">⭐ {child.metadata.imdb_rating}</Box>
                    )}
                    {child.metadata?.duration && (
                      <Box color="text-body-secondary">{child.metadata.duration}</Box>
                    )}
                  </SpaceBetween>
                </Box>
              ))}
            </SpaceBetween>
          </Container>
        )}

        {(title.metadata?.imdb_rating || title.metadata?.actors || title.metadata?.director) && (
          <Container header={<Box variant="h3">IMDb Information</Box>}>
            <ColumnLayout columns={3}>
              <div>
                <Box variant="awsui-key-label">Rating</Box>
                <Box>⭐ {title.metadata.imdb_rating}/10</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Votes</Box>
                <Box>{title.metadata.imdb_votes || '-'}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Box Office</Box>
                <Box>{title.metadata.box_office || '-'}</Box>
              </div>
              {title.metadata.director && (
                <div>
                  <Box variant="awsui-key-label">Director</Box>
                  <Box>{title.metadata.director}</Box>
                </div>
              )}
              {title.metadata.actors && (
                <div>
                  <Box variant="awsui-key-label">Actors</Box>
                  <Box>{title.metadata.actors}</Box>
                </div>
              )}
              {title.metadata.awards && (
                <div>
                  <Box variant="awsui-key-label">Awards</Box>
                  <Box>{title.metadata.awards}</Box>
                </div>
              )}
            </ColumnLayout>
          </Container>
        )}

        {(title.external_ids && Object.keys(title.external_ids).length > 0) && (
          <Container header={<Box variant="h3">External IDs</Box>}>
            <ColumnLayout columns={3}>
              {Object.entries(title.external_ids).map(([system, id]) => (
                id && (
                  <div key={system}>
                    <Box variant="awsui-key-label">{system.replace('_', ' ').toUpperCase()}</Box>
                    <Box>{id}</Box>
                  </div>
                )
              ))}
            </ColumnLayout>
          </Container>
        )}

        {(title.reference_ids && Object.keys(title.reference_ids).length > 0) && (
          <Container header={<Box variant="h3">Reference IDs</Box>}>
            <ColumnLayout columns={3}>
              {Object.entries(title.reference_ids).map(([system, id]) => (
                id && (
                  <div key={system}>
                    <Box variant="awsui-key-label">{system.replace('_', ' ').toUpperCase()}</Box>
                    <Box>
                      {system === 'imdb_id' ? (
                        <Link external href={`https://www.imdb.com/title/${id}`}>
                          {id}
                        </Link>
                      ) : (
                        id
                      )}
                    </Box>
                  </div>
                )
              ))}
            </ColumnLayout>
          </Container>
        )}
        
        <Tabs
          tabs={[
            {
              label: "Details",
              id: "details",
              content: <Box>Content details are shown above</Box>
            },
            {
              label: "Versions",
              id: "versions",
              content: <VersionsTab titleId={id} />
            }
          ]}
        />
      </SpaceBetween>
    </ContentLayout>
    <Modal
      visible={showImdbModal}
      onDismiss={() => { setShowImdbModal(false); setImdbError(''); setImdbId(''); }}
      header={`Add child from IMDb — ${title.metadata?.title || title.title_id}`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => { setShowImdbModal(false); setImdbError(''); setImdbId(''); }}>
              Cancel
            </Button>
            <Button variant="primary" loading={imdbLoading} onClick={handleAddFromImdb} disabled={!imdbId.trim()}>
              Create from IMDb
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {imdbError && <Alert type="error">{imdbError}</Alert>}
        <FormField
          label="IMDb ID"
          description="Enter the IMDb ID (e.g. tt1234567) to create a child title with enriched metadata"
        >
          <Input
            value={imdbId}
            onChange={({ detail }) => setImdbId(detail.value)}
            placeholder="tt1234567"
            disabled={imdbLoading}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
    </>
  );
};

export default TitleDetail;